import React, { useState, useEffect } from 'react';
import { Eye, CheckCircle, XCircle, Clock, Download, Server, Cpu, Calendar, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../config';

const AdminClusterLicenses = () => {
  const [clusterLicenses, setClusterLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('ALL');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;
  
  // Configuration form state
  const [licenseType, setLicenseType] = useState('Production');
  const [licenseTier, setLicenseTier] = useState('Standard');
  const [expiresAt, setExpiresAt] = useState('');
  const [empReferenceCode, setEmpReferenceCode] = useState('');
  const [responseNotes, setResponseNotes] = useState('');

  // Set page title
  useEffect(() => {
    document.title = 'Cluster Licenses | Admin Portal';
  }, []);

  useEffect(() => {
    fetchClusterLicenses();
    setCurrentPage(1); // Reset to page 1 when filter changes
  }, [filterStatus]);

  const fetchClusterLicenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filterStatus === 'ALL' 
        ? `${API_URL}/cluster-licenses`
        : `${API_URL}/cluster-licenses?status=${filterStatus}`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setClusterLicenses(data);
      }
    } catch (error) {
      console.error('Failed to fetch cluster licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cluster-licenses/${id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedLicense(data);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Failed to fetch details:', error);
      toast.error('Failed to load cluster license details');
    }
  };

  const downloadLicense = async (id, hostname) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cluster-licenses/download/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get the file as a blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cluster-license-${hostname}-signed.xlic`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('License downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download license file');
    }
  };

  const openConfigModal = (license) => {
    setSelectedLicense(license);
    
    // Set default expiration to 1 year from now
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    setExpiresAt(oneYearFromNow.toISOString().split('T')[0]);
    
    // Set default deployment instructions
    const defaultInstructions = `Deployment Instructions:

1. Download the signed cluster license file
2. Open XDeploy → Navigate to xavs-license module
3. Upload the .xlic file to activate cluster license
4. Verify license status in the dashboard

The xavs-license module will automatically distribute and activate the license across all cluster nodes.`;
    
    // Reset form
    setLicenseType('Production');
    setLicenseTier('Standard');
    setEmpReferenceCode('');
    setResponseNotes(defaultInstructions);
    
    setShowConfigModal(true);
  };

  const configureAndSign = async () => {
    if (!licenseType || !licenseTier || !expiresAt || !empReferenceCode) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate expiration date
    const expDate = new Date(expiresAt);
    if (expDate <= new Date()) {
      toast.error('Expiration date must be in the future');
      return;
    }

    if (!confirm(`Are you sure you want to sign this cluster license?\n\nType: ${licenseType}\nTier: ${licenseTier}\nExpires: ${expDate.toLocaleDateString()}\n\nThis will generate a signed license file and send it to the customer.`)) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cluster-licenses/${selectedLicense.id}/configure-and-sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          licenseType,
          licenseTier,
          expiresAt,
          empReferenceCode,
          responseNotes,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Cluster license configured and signed successfully! The customer will receive an email with the signed license file.');
        setShowConfigModal(false);
        setShowDetailsModal(false);
        setSelectedLicense(null);
        fetchClusterLicenses();
      } else {
        toast.error(data.error || 'Failed to configure and sign cluster license');
      }
    } catch (error) {
      console.error('Configure and sign error:', error);
      toast.error('Failed to configure and sign cluster license');
    } finally {
      setProcessing(false);
    }
  };

  const rejectLicense = async (id) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/cluster-licenses/${id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ responseNotes: reason }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Cluster license request rejected. The customer will be notified.');
        setShowDetailsModal(false);
        setSelectedLicense(null);
        fetchClusterLicenses();
      } else {
        toast.error(data.error || 'Failed to reject cluster license');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject cluster license');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        icon: Clock,
        className: 'bg-yellow-100 text-yellow-800',
        label: 'Pending Configuration',
      },
      APPROVED: {
        icon: CheckCircle,
        className: 'bg-green-100 text-green-800',
        label: 'Signed',
      },
      REJECTED: {
        icon: XCircle,
        className: 'bg-red-100 text-red-800',
        label: 'Rejected',
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const pendingCount = clusterLicenses.filter(l => l.status === 'PENDING').length;

  // Pagination calculations
  const totalPages = Math.ceil(clusterLicenses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLicenses = clusterLicenses.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Cluster Licenses</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure and sign cluster license requests for multi-node deployments
          </p>
        </div>
        {pendingCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            <span className="text-amber-800 font-medium text-sm">{pendingCount} pending configuration</span>
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
        <div className="flex space-x-2">
          {['ALL', 'PENDING', 'APPROVED', 'REJECTED'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              style={filterStatus === status ? { backgroundColor: '#197560' } : {}}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Cluster Licenses Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {clusterLicenses.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            <Server className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p>No cluster licenses found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Hostname
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Cluster Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    License Config
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Submitted
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedLicenses.map((license) => (
                  <tr key={license.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {license.user?.firstName || 'N/A'} {license.user?.lastName || ''}
                        </div>
                        <div className="text-xs text-gray-500">{license.user?.email || 'N/A'}</div>
                        {license.user?.company && (
                          <div className="text-xs text-gray-500">{license.user.company}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{license.hostname || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{license.filename}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3 text-xs">
                        <div className="flex items-center">
                          <Server className="w-3 h-3 mr-1 text-gray-400" />
                          <span>{license.totalHosts} host{license.totalHosts !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center">
                          <Cpu className="w-3 h-3 mr-1 text-gray-400" />
                          <span>{license.totalCores} cores</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {license.licenseType ? (
                        <div className="text-xs">
                          <div className="font-medium">{license.licenseType} / {license.licenseTier}</div>
                          {license.expiresAt && (
                            <div className="text-gray-500">
                              Expires: {new Date(license.expiresAt).toLocaleDateString()}
                            </div>
                          )}
                          {license.empReferenceCode && (
                            <div className="text-gray-500">Ref: {license.empReferenceCode}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not configured</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(license.status)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatDate(license.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => viewDetails(license.id)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {license.status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => openConfigModal(license)}
                              className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                              title="Configure & Sign"
                            >
                              Configure
                            </button>
                            <button
                              onClick={() => rejectLicense(license.id)}
                              className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
                              title="Reject"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        
                        {license.signedFilePath && (
                          <button
                            onClick={() => downloadLicense(license.id, license.hostname)}
                            className="text-green-600 hover:text-green-800 p-1"
                            title="Download Signed License"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {clusterLicenses.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, clusterLicenses.length)}</span> of{' '}
              <span className="font-medium">{clusterLicenses.length}</span> licenses
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={currentPage === page ? { backgroundColor: '#197560' } : {}}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      currentPage === page
                        ? 'text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && selectedLicense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Configure & Sign Cluster License</h2>
              
              <div className="mb-4 p-4 bg-gray-50 rounded">
                <h3 className="font-semibold mb-2">Customer Information</h3>
                <div className="text-sm space-y-1">
                  <div><strong>Name:</strong> {selectedLicense.user?.firstName} {selectedLicense.user?.lastName}</div>
                  <div><strong>Email:</strong> {selectedLicense.user?.email}</div>
                  <div><strong>Hostname:</strong> {selectedLicense.hostname}</div>
                  <div><strong>Total Hosts:</strong> {selectedLicense.totalHosts}</div>
                  <div><strong>Total Cores:</strong> {selectedLicense.totalCores}</div>
                </div>
              </div>

              <div className="space-y-4">
                {/* License Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    License Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={licenseType}
                    onChange={(e) => setLicenseType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  >
                    <option value="PoC">PoC (Proof of Concept)</option>
                    <option value="Trial">Trial</option>
                    <option value="Production">Production</option>
                  </select>
                </div>

                {/* License Tier */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    License Tier <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={licenseTier}
                    onChange={(e) => setLicenseTier(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  >
                    <option value="Standard">Standard</option>
                    <option value="Professional">Professional</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>

                {/* Expiration Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiration Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                    />
                    <Calendar className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Employee Reference Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Reference Code <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={empReferenceCode}
                      onChange={(e) => setEmpReferenceCode(e.target.value)}
                      placeholder="e.g., EMP-2025-XAVS-1234"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                    />
                    <FileText className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Response Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Response Notes (Optional)
                  </label>
                  <textarea
                    value={responseNotes}
                    onChange={(e) => setResponseNotes(e.target.value)}
                    placeholder="Any additional notes for the customer..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfigModal(false)}
                  disabled={processing}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={configureAndSign}
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Configure & Sign License'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal (same as before but adapted for cluster licenses) */}
      {showDetailsModal && selectedLicense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Cluster License Details</h2>
              
              {/* Customer Info */}
              <div className="mb-4 p-4 bg-gray-50 rounded">
                <h3 className="font-semibold mb-2">Customer Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>Name:</strong> {selectedLicense.user?.firstName} {selectedLicense.user?.lastName}</div>
                  <div><strong>Email:</strong> {selectedLicense.user?.email}</div>
                  <div><strong>Company:</strong> {selectedLicense.user?.company || 'N/A'}</div>
                  <div><strong>Phone:</strong> {selectedLicense.user?.phone || 'N/A'}</div>
                </div>
              </div>

              {/* Cluster Info */}
              <div className="mb-4 p-4 bg-blue-50 rounded">
                <h3 className="font-semibold mb-2">Cluster Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>Primary Hostname:</strong> {selectedLicense.hostname}</div>
                  <div><strong>Total Hosts:</strong> {selectedLicense.totalHosts}</div>
                  <div><strong>Total CPU Sockets:</strong> {selectedLicense.totalSockets}</div>
                  <div><strong>Total CPU Cores:</strong> {selectedLicense.totalCores}</div>
                </div>
              </div>

              {/* Hosts Details */}
              {selectedLicense.hostsData && Object.keys(selectedLicense.hostsData).length > 0 && (
                <div className="mb-4 p-4 bg-gray-50 rounded">
                  <h3 className="font-semibold mb-2">Host Details</h3>
                  <div className="space-y-2">
                    {Object.entries(selectedLicense.hostsData).map(([hostname, hostInfo]) => (
                      <div key={hostname} className="border-l-4 border-blue-500 pl-3 text-sm">
                        <div className="font-medium">{hostname}</div>
                        <div className="text-gray-600 text-xs">
                          UUID: {hostInfo.product_uuid} | 
                          Sockets: {hostInfo.cpu_sockets} | 
                          Cores: {hostInfo.cpu_cores}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedLicense.notes && (
                <div className="mb-4 p-4 bg-yellow-50 rounded">
                  <h3 className="font-semibold mb-2">Customer Notes</h3>
                  <p className="text-sm">{selectedLicense.notes}</p>
                </div>
              )}

              {/* Full Decrypted Data */}
              {selectedLicense.decryptedData && (
                <div className="mb-4">
                  <details className="bg-gray-50 rounded-lg p-4">
                    <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                      View Full Data (JSON)
                    </summary>
                    <pre className="mt-3 bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">
                      {JSON.stringify(selectedLicense.decryptedData, null, 2)}
                    </pre>
                  </details>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Close
                </button>
                {selectedLicense.status === 'PENDING' && (
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openConfigModal(selectedLicense);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Configure & Sign
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClusterLicenses;
